import { ReactElement, useCallback, useEffect, useState } from 'react';

import {
  TertiaryButton,
  TertiaryButtonProps,
} from '@changegps/horizon-component-library';
import { CircularProgress, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

import { getXeroInvoiceConnect } from '../api';
import {
  useXeroInvoiceConnect,
  useXeroInvoiceDisconnect,
  useXeroInvoiceOAuthCompletion,
  useXeroInvoiceStatus,
} from '../hooks';

type XeroInvoiceToggleButtonProps = TertiaryButtonProps;

type XeroConnectResponse = Awaited<ReturnType<typeof getXeroInvoiceConnect>>;

export const XeroInvoiceToggleButton = (
  props: XeroInvoiceToggleButtonProps,
): ReactElement => {
  const [oauthParams, setOauthParams] = useState<{
    code?: string;
    company_id?: string;
  }>({});
  const [hasCompletedOAuth, setHasCompletedOAuth] = useState(false);

  const location = useLocation();
  const { mutate: connect, isPending: isConnecting } = useXeroInvoiceConnect();
  const { mutate: disconnect, isPending: isDisconnecting } =
    useXeroInvoiceDisconnect();
  const { data: oauthCompletionData } =
    useXeroInvoiceOAuthCompletion(oauthParams);
  const xeroInvoiceStatus = useXeroInvoiceStatus();

  // Detect OAuth callback parameters from URL
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const code = queryParams.get('code');
    const state = queryParams.get('state');
    const company_id = queryParams.get('company_id');

    if (state === 'invoice') {
      if (code) {
        setOauthParams({
          code: code || undefined,
          company_id: company_id || undefined,
        });
      }

      // Reset the URL after success or error
      window.history.replaceState(null, '', '/settings/integrations');
    }
  }, [location]);

  // Auto-refresh status when OAuth completes
  useEffect(() => {
    if (oauthCompletionData && !hasCompletedOAuth) {
      toast.success('Xero Invoice connected successfully');
      void xeroInvoiceStatus.refetch();
      setHasCompletedOAuth(true);
    }
  }, [oauthCompletionData, hasCompletedOAuth, xeroInvoiceStatus]);

  const handleConnect = useCallback(() => {
    // Clear stale OAuth data and reset the completion flag to allow new connection
    setOauthParams({});
    setHasCompletedOAuth(false);

    connect(undefined, {
      onSuccess: (responseData: XeroConnectResponse) => {
        if ('authUrl' in responseData) {
          if (responseData.already_connected) {
            toast.info('Already connected to Xero Invoice');
            void xeroInvoiceStatus.refetch();
          } else {
            // Add state parameter to identify Xero Invoice integration
            const urlWithState = new URL(responseData.authUrl);
            urlWithState.searchParams.set('state', 'invoice');
            window.location.replace(urlWithState.toString());
          }
        }
      },
      onError: () => {
        toast.error('Error connecting Xero Invoice');
      },
    });
  }, [connect, xeroInvoiceStatus]);

  const handleDisconnect = useCallback(() => {
    disconnect(undefined, {
      onSuccess: () => {
        toast.success('Xero Invoice disconnected');
        void xeroInvoiceStatus.refetch();
      },
      onError: () => {
        toast.error('Error disconnecting Xero Invoice');
      },
    });
  }, [disconnect, xeroInvoiceStatus]);

  const isConnected = Boolean(xeroInvoiceStatus.data?.isConnected);

  return (
    <TertiaryButton
      {...props}
      onClick={() => {
        isConnected ? handleDisconnect() : handleConnect();
      }}
    >
      <Content
        isLoading={isConnecting || isDisconnecting}
        isConnected={isConnected}
      />
    </TertiaryButton>
  );
};

type ContentProps = {
  isConnected: boolean;
  isLoading: boolean;
};

const Content = (props: ContentProps): ReactElement => {
  if (props.isLoading) return <CircularProgress size={16} />;

  if (props.isConnected) return <Typography fontSize={13}>Disable</Typography>;

  return <Typography fontSize={13}>Enable</Typography>;
};
